import React, { useRef, useState, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { Box, Grow, IconButton, Paper, Typography, Chip, Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import saleforce from '../images/salesforceDest.png';
import oracle from '../images/oracleDest.png';
import sql from '../images/sqlDest.png';
import hubspot from '../images/hubspotdes.png';
import { useTheme } from '@mui/material/styles';
import salesforceIcon from '../images/salesforce.png';
import oracleIcon from '../images/oracle.png';
import hubspotIcon from '../images/hubspot.png';
import sqlIcon from '../images/sql.png';
import kafkaIcon from '../images/Streaming-Kafka.png';
import s3Icon from '../images/Streamin-S3.png';
import localFileIcon from '../images/Streming LocalFile.png';
import destIcon from '../images/destIcon.png';
import AddIcon from '@mui/icons-material/Add';

const DestinationNode = ({ id, data, deleteNode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isHandleHover, setIsHandleHover] = useState(false);
  const hoverTimeout = useRef(null);


  const muiTheme = useTheme();

  const nodeFields = useMemo(() => {
    return data?.fields || [];
  }, [data?.fields]);

  const autoMappedFields = useMemo(() => {
    return data?.autoMappedFields || {};
  }, [data?.autoMappedFields]);

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 400);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    setIsHovered(false);
    setIsHandleHover(false);
  };

  if (data?.isPlaceholder) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          cursor: 'pointer',
          px: 1,
          py: 0.5,
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '3px',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: '1px solid #E6E6E6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '2px',
              backgroundColor: '#3C9362',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: '#0F172B',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {data.placeholderLabel || 'Select Destination'}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '0.75rem',
            color: '#62748E',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {data.placeholderHint || 'Choose your data destination'}
        </Typography>
      </Box>
    );
  }

  const [mainLabel] = data.label.includes('(')
    ? data.label.split(' (')
    : [data.label, ''];

  // Note: typeLabel is extracted but not used in this component.
  // ETLWorkflow.jsx parses the label format directly from node.data.label
  // using regex: node.data.label.match(/(.*?)\s*\((.*?)\)/)
  // The label format "ConnectorName (Destination)" is preserved for compatibility.

  // Determine connector type
  const getConnectorType = (label) => {
    const normalized = (label || '').toLowerCase();
    // Streaming connectors
    if (normalized === 'kafka' || normalized === 's3' || normalized === 'local file' || normalized === 'sftp') {
      return 'Streaming connector';
    }
    // Database connectors
    if (normalized === 'sql' || normalized === 'oracle' || normalized.includes('sql') || normalized.includes('oracle')) {
      return 'Database connector';
    }
    // SaaS connectors (default)
    return 'SaaS connector';
  };

  const connectorType = getConnectorType(mainLabel);

  //eslint-disable-next-line
  const icon =
    mainLabel === 'Salesforce' ? saleforce 
    : mainLabel === 'HubSpot' ? hubspot 
    : mainLabel === 'SQL' ? sql 
    : mainLabel === 'Kafka' ? kafkaIcon
    : mainLabel === 'S3' ? s3Icon
    : mainLabel === 'Local File' ? localFileIcon
    : oracle;
  const eachIcon =
    mainLabel === 'Salesforce' ? salesforceIcon 
    : mainLabel === 'HubSpot' ? hubspotIcon 
    : mainLabel === 'SQL' ? sqlIcon 
    : mainLabel === 'Kafka' ? kafkaIcon
    : mainLabel === 'S3' ? s3Icon
    : mainLabel === 'Local File' ? localFileIcon
    : oracleIcon;

  const showOverlay = isHovered && !isHandleHover;

  // Check if field is auto-mapped
  const isFieldAutoMapped = (fieldName) => {
    if (!autoMappedFields || typeof autoMappedFields !== 'object') return false;
    return Object.values(autoMappedFields).some(
      (value) => value.includes(fieldName) || fieldName.includes(value.split('.').pop())
    );
  };

  const hasFields = nodeFields && nodeFields.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          minWidth: 140,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: '6px',
          backgroundColor: '#fff',
          border: '1px solid #E0E0E0',
          boxShadow: isHovered ? '0 4px 12px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
          zIndex: isHovered ? 100 : 1,
          transition: 'box-shadow 0.25s ease, transform 0.25s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
          },
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Icon Container */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '4px',
            backgroundColor: '#F5F6FA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            flexShrink: 0,
          }}
        >
          <img 
            src={eachIcon} 
            alt={mainLabel} 
            style={{
              width: '32px',
              height: '32px',
              objectFit: 'contain',
            }}
          />
        </Box>
        
        {/* Text Labels */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 400,
              color: '#6B7280',
              lineHeight: 1.2,
              marginBottom: '2px',
            }}
          >
            Destination
          </Typography>
          <Typography
            sx={{
              fontFamily: 'IBM Plex Sans, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#111827',
              lineHeight: 1.2,
            }}
          >
            {mainLabel}
          </Typography>
        </Box>

      <Grow
        in={showOverlay}
        timeout={{ enter: 300, exit: 250 }}
        style={{ transitionDelay: showOverlay ? '100ms' : '0ms' }}
      >
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: -8,
            left: -12,
            minWidth: 200,
            borderRadius: '6px',
            bgcolor: '#fff',
            border: '1px solid #E0E0E0',
            zIndex: 200,
            transition: 'opacity 0.15s',
            pointerEvents: 'auto',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            '&:hover': { 
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)' 
            },
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
              }}
            >
              <img 
                src={destIcon} 
                alt="Destination" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
              }}
            >
              Destination
            </Typography>
          </Box>

          {/* Content Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '4px',
                backgroundColor: '#E8FAF4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <img 
                src={eachIcon} 
                alt={mainLabel} 
                style={{
                  width: '28px',
                  height: '28px',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#111827',
                  lineHeight: 1.2,
                  marginBottom: '2px',
                }}
              >
                {mainLabel}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  color: '#6B7280',
                  lineHeight: 1.2,
                }}
              >
                {connectorType}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(id, data.label);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: '#fff',
              color: muiTheme.palette.error.main,
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '50%',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': {
                background: muiTheme.palette.error.dark,
                color: '#fff',
              },
            }}
          >
            <Close sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        </Paper>
      </Grow>

      {(!isHovered || isHandleHover) && (
        <Handle
          type="target"
          position={Position.Left}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ zIndex: 300 }}
        />
      )}
      {(!isHovered || isHandleHover) && (
        <Handle
          type="source"
          position={Position.Right}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ zIndex: 300 }}
        />
      )}
      </Box>

      {/* Field List Below Node */}
      {hasFields && (
        <Box
          sx={{
            minWidth: 120,
            maxWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mt: 0.5,
            p: 1,
            bgcolor: '#f5f6fa',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
          }}
        >
          {nodeFields.slice(0, 5).map((field, idx) => {
            const fieldName = typeof field === 'string' ? field : field?.name || field?.field_name || '';
            const isAutoMapped = isFieldAutoMapped(fieldName);
            
            return (
              <Chip
                key={idx}
                label={fieldName}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  bgcolor: isAutoMapped ? '#e8f5e9' : 'transparent',
                  color: isAutoMapped ? '#2e7d32' : '#383838',
                  border: isAutoMapped ? '1px solid #4caf50' : '1px solid #e0e0e0',
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  },
                }}
              />
            );
          })}
          {nodeFields.length > 5 && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: '#62748E',
                textAlign: 'center',
                mt: 0.5,
              }}
            >
              +{nodeFields.length - 5} more
            </Typography>
          )}
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{
              mt: 0.5,
              fontSize: '0.7rem',
              textTransform: 'none',
              color: '#549bde',
              '&:hover': {
                bgcolor: 'rgba(84, 155, 222, 0.1)',
              },
            }}
          >
            Add Entity
          </Button>
        </Box>
      )}
    </Box>
  );
};

DestinationNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    isPlaceholder: PropTypes.bool,
    placeholderLabel: PropTypes.string,
    placeholderHint: PropTypes.string,
    placeholderKind: PropTypes.string,
    fields: PropTypes.array, // Array of field names/objects
    autoMappedFields: PropTypes.object, // Object mapping source to destination fields
  }).isRequired,
  deleteNode: PropTypes.func.isRequired,
};

export default DestinationNode;
